import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import DOMPurify from "dompurify";
import normalizeDescriptionHtml from "../utils/descriptionHtml";
import {
  addToCart,
  removeFromCart,
} from "../redux/retailerWholesalerCartSlice";
import { viewItem, addToCart as trackAddToCart } from "../utils/analytics";
import {
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Carousel from "react-material-ui-carousel";
import Reviews from "../components/Reviews/Reviews";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { formatCurrency } from "../config/market";
import { buildVendorParam } from "../utils/vendorSlug";
import { buildYouTubeEmbedUrl } from "../utils/productVideos";
import DealTag from "../components/DealTag";
import PriceCut from "../components/PriceCut";
import { hasSalePrice, unitPriceOf, unitSavingsOf } from "../utils/vendorDiscounts";

const RetailerWholesalerProductModal = ({ open, onClose, product }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentVendor = useSelector(
    (state) => state.retailerWholesalerCart.currentVendor
  );
  const vendor = useSelector((state) => state.wholesaleRetailVendor.vendor);
  const cart = useSelector(
    (state) => state.retailerWholesalerCart.carts[currentVendor] || {}
  );
  const cartItem = cart[product._id] || {};

  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  // Helper function to extract public ID from Cloudinary URL
  const getCloudinaryPublicId = (url) => {
    // Example URL: https://res.cloudinary.com/lusuku/video/upload/v1735679292/Capping_Machine_aolxoj.mp4
    try {
      const urlParts = url.split("/");
      // Find the index after 'upload'
      const uploadIndex = urlParts.findIndex((part) => part === "upload");
      if (uploadIndex === -1) return "";

      // Get everything after 'upload' except the version and file extension
      const publicId = urlParts
        .slice(uploadIndex + 2) // Skip the version number
        .join("/")
        .replace(/\.[^/.]+$/, ""); // Remove file extension

      return publicId;
    } catch (error) {
      console.error("Error parsing Cloudinary URL:", error);
      return "";
    }
  };

  const renderMediaItem = (item) => {
    if (item.type === "youtube") {
      return (
        <VideoContainer>
          <YouTubeEmbedFrame
            src={buildYouTubeEmbedUrl(item.url, { autoplay: false })}
            title={`${product.name} video`}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </VideoContainer>
      );
    }
    if (item.type === "video") {
      const publicId = getCloudinaryPublicId(item.url);
      console.log("Video public ID:", publicId); // Debug log

      return (
        <VideoContainer>
          {!isPlaying && (
            <VideoOverlay
              onClick={() => {
                if (playerRef.current) {
                  playerRef.current.play();
                } else {
                  console.log("Player not initialized"); // Debug log
                }
              }}
            >
              <PlayCircleOutlineIcon style={{ fontSize: 60 }} />
            </VideoOverlay>
          )}
          <video
            ref={videoRef}
            className="cld-video-player cld-fluid"
            controls
            data-cld-public-id={publicId}
          />
        </VideoContainer>
      );
    }
    return <MediaImage src={item.url} alt={product.name} />;
  };

  // Fire viewItem analytics when modal opens
  useEffect(() => {
    if (open && product?._id) {
      viewItem({ id: product._id, name: product.name, price: product.price });
    }
  }, [open, product?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize player when modal is opened and video element exists
  useEffect(() => {
    if (open && videoRef.current && window.cloudinary && !playerRef.current) {
      try {
        console.log("Initializing Cloudinary player..."); // Debug log
        playerRef.current = window.cloudinary.videoPlayer(videoRef.current, {
          cloud_name: "lusuku",
          controls: true,
          fluid: true,
          playsinline: true,
          muted: false,
          preload: "auto",
          sourceTypes: ["webm", "mp4"],
          posterOptions: {
            transformation: {
              width: 960,
              crop: "scale",
            },
          },
        });

        // Add event listeners
        playerRef.current.on("play", () => {
          console.log("Video playing"); // Debug log
          setIsPlaying(true);
        });
        playerRef.current.on("pause", () => {
          console.log("Video paused"); // Debug log
          setIsPlaying(false);
        });
        playerRef.current.on("ended", () => setIsPlaying(false));

        // Log success
        console.log("Player initialized successfully");
      } catch (error) {
        console.error("Error initializing Cloudinary player:", error);
      }
    }

    return () => {
      if (playerRef.current) {
        console.log("Disposing player..."); // Debug log
        playerRef.current.dispose();
        playerRef.current = null;
      }
      setIsPlaying(false);
    };
  }, [open]);

  const handleAddToCart = () => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: 1 }));
    trackAddToCart({ id: product._id, name: product.name, price: unitPriceOf(product), quantity: 1 });
  };

  const handleIncrease = () => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: 1 }));
    trackAddToCart({ id: product._id, name: product.name, price: unitPriceOf(product), quantity: 1 });
  };

  const handleDecrease = () => {
    dispatch(addToCart({ vendorId: currentVendor, product, quantity: -1 }));
    if (cartItem.quantity === 1) {
      dispatch(
        removeFromCart({ vendorId: currentVendor, productId: product._id })
      );
    }
  };

  /* Straight to the unified Cart Hub (/cart) — it groups this vendor's
     items with any others, and its per-group Checkout routes back into
     the RW flow. Close the modal first so it isn't left open behind the
     route change / on back-navigation. */
  const handleGoToCart = () => {
    onClose();
    history.push("/cart");
  };

  /* Deep-link to the dedicated product page (Amazon-style layout with
     image zoom + related products). Product travels via location.state
     for an instant paint; the page re-resolves it from the vendor fetch
     so refresh/shared links also work. */
  const handleViewFullDetails = () => {
    const shopSlug = buildVendorParam(vendor?.name, currentVendor);
    const productSlug = buildVendorParam(product?.name, product?._id);
    onClose();
    history.push({
      pathname: `/shop/${shopSlug}/product/${productSlug}`,
      state: { product },
    });
  };

  // const handleVideoPlay = () => {
  //   if (videoRef.current) {
  //     videoRef.current.play();
  //     setIsPlaying(true);
  //   }
  // };

  // const handleVideoPause = () => {
  //   if (videoRef.current) {
  //     videoRef.current.pause();
  //     setIsPlaying(false);
  //   }
  // };

  /* Combine images and videos into a single media array. The cover
     image (product.image) leads, followed by any extra angles
     (product.additionalImages, the new field) and finally videos.
     Falsy entries are filtered out so an empty `image` doesn't render
     a blank slide. */
  const mediaItems = [
    ...(typeof product.image === "string" && product.image
      ? [{ type: "image", url: product.image }]
      : product.images?.map((img) => ({ type: "image", url: img })) || []),
    ...((Array.isArray(product.additionalImages)
      ? product.additionalImages
      : []
    )
      .filter((u) => typeof u === "string" && u)
      .map((u) => ({ type: "image", url: u }))),
    /* `videos` carries either legacy Cloudinary URL strings or
       contract-shaped objects ({ kind, url, thumbnailUrl, ... } — max 3,
       kind CLOUDINARY | YOUTUBE). Map both to carousel slides: Cloudinary
       clips keep the existing cld video player, YouTube entries get an
       iframe embed slide. Malformed entries are dropped. */
    ...((Array.isArray(product.videos) ? product.videos : []).flatMap(
      (video) => {
        if (typeof video === "string" && video) {
          return [{ type: "video", url: video }];
        }
        if (video && typeof video === "object" && typeof video.url === "string" && video.url) {
          if (video.kind === "YOUTUBE") {
            return buildYouTubeEmbedUrl(video.url)
              ? [{ type: "youtube", url: video.url }]
              : [];
          }
          return [{ type: "video", url: video.url }];
        }
        return [];
      }
    )),
  ];

  // Reset video state when modal closes or carousel slides change
  useEffect(() => {
    if (!open && playerRef.current) {
      playerRef.current.pause();
      setIsPlaying(false);
    }
  }, [open]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <ModalHeader>
        <HeaderLeftGroup>
          <ProductBadge>Premium Product</ProductBadge>
          {/* Prominent, above-the-fold entry point to the dedicated
              product page (the in-body link near Add-to-Cart remains
              as a contextual second entry point). */}
          <HeaderDetailsButton onClick={handleViewFullDetails}>
            <OpenInFullIcon style={{ fontSize: 14 }} />
            Full details
          </HeaderDetailsButton>
        </HeaderLeftGroup>
        <CloseButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </CloseButton>
      </ModalHeader>

      <StyledDialogContent>
        {mediaItems.length > 0 && (
          <CarouselContainer>
            {hasSalePrice(product) && (
              <DealTag
                discount={product.discount}
                size="l"
                radius={0}
                zIndex={2}
              />
            )}
            <Carousel
              animation="slide"
              autoPlay={false}
              indicators={mediaItems.length > 1}
              navButtonsAlwaysVisible={mediaItems.length > 1}
              onChange={() => setIsPlaying(false)}
            >
              {mediaItems.map((item, index) => (
                <MediaWrapper key={index}>{renderMediaItem(item)}</MediaWrapper>
              ))}
            </Carousel>
          </CarouselContainer>
        )}

        <ProductDetails>
          <ProductTitle>{product.name}</ProductTitle>

          <ProductDescription>
            <DescriptionLabel>
              <DescriptionOutlinedIcon style={{ fontSize: 16 }} />
              Description
            </DescriptionLabel>
            {product.descriptionHtml ? (
              /* Vendor-authored rich description (standardized Quill
                 formats). Sanitized server-side on write AND DOMPurified
                 here at render — defence in depth. */
              <RichDescriptionText
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    normalizeDescriptionHtml(product.descriptionHtml)
                  ),
                }}
              />
            ) : (
              <DescriptionText>{product.description}</DescriptionText>
            )}
          </ProductDescription>

          <PriceSection>
            <PriceLabel>Price</PriceLabel>
            <PriceCut
              component={PriceValue}
              price={product.price}
              listPrice={product.listPrice}
              salePrice={product.salePrice}
              format={formatCurrency}
              savings={unitSavingsOf(product)}
              endsAt={product.discount?.endsAt}
              align="end"
            />
          </PriceSection>

          <QuantitySection>
            <QuantityLabel>Quantity</QuantityLabel>
            <QuantityControls>
              <QuantityButton
                onClick={handleDecrease}
                disabled={!cartItem.quantity}
                className="decrease"
              >
                <RemoveIcon />
              </QuantityButton>
              <QuantityDisplay>{cartItem.quantity || 0}</QuantityDisplay>
              <QuantityButton onClick={handleIncrease} className="increase">
                <AddIcon />
              </QuantityButton>
            </QuantityControls>
          </QuantitySection>

          <ActionButtons>
            <AddToCartButton
              onClick={handleAddToCart}
              disabled={cartItem.quantity > 0}
              className={cartItem.quantity > 0 ? 'in-cart' : ''}
            >
              {cartItem.quantity > 0 ? (
                <>
                  <span className="icon in-cart-icon">
                    <CheckCircleIcon style={{ fontSize: 20, color: '#fff' }} />
                  </span>
                  Item in Cart
                </>
              ) : (
                <>
                  <span className="icon">🛒</span>
                  Add to Cart
                </>
              )}
            </AddToCartButton>
            {cartItem.quantity > 0 && (
              <GoToCartButton onClick={handleGoToCart}>
                <ShoppingCartIcon style={{ fontSize: 18 }} />
                Go to cart
              </GoToCartButton>
            )}
            <ViewDetailsLink onClick={handleViewFullDetails}>
              <OpenInFullIcon style={{ fontSize: 15 }} />
              View full details — larger images, zoom &amp; related products
            </ViewDetailsLink>
          </ActionButtons>
        </ProductDetails>
        {product?._id && (
          <div style={{ padding: "0 24px 20px" }}>
            <Reviews
              subjectType="product_retailer"
              subjectId={product._id}
              title="Customer reviews"
            />
          </div>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default RetailerWholesalerProductModal;

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.3);
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #042940 0%, #03506f 100%);
  padding: 20px 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 12px rgba(4, 41, 64, 0.15);
`;

const HeaderLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeaderDetailsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    border-color: white;
    transform: translateY(-1px);
  }
`;

const ProductBadge = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #2c3e50;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
  font-family: 'Poppins', sans-serif;
`;

const CloseButton = styled(IconButton)`
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3) !important;
    transform: rotate(90deg) scale(1.1);
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 0 !important;
  background: #f8f9fa;
`;

const ProductDetails = styled.div`
  padding: 14px;
  background: white;
`;

const ProductTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 18px 0;
  font-family: 'Poppins', sans-serif;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, #f24405 0%, #ff6b35 100%);
    border-radius: 2px;
  }
`;

const ProductDescription = styled.div`
  margin: 18px 0;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 10px;
  border-left: 3px solid #84b026;
`;

const DescriptionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #042940;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Poppins', sans-serif;
  
  .icon {
    font-size: 16px;
  }
`;

const DescriptionText = styled.p`
  font-family: "Urbanist", "Inter", "Segoe UI", sans-serif;
  font-size: 13.5px;
  line-height: 1.65;
  color: #444;
  margin: 0;
  white-space: pre-line;
`;

/* Uniform rendering for Quill-authored rich descriptions. Mirrors the
   CANONICAL spec on the dedicated product page (`RichDescription` in
   RetailerWholesalerProductPage.js), which the dashboard editor also mirrors
   (richText.js DESCRIPTION_TYPOGRAPHY_CSS) — change all three together. */
const RichDescriptionText = styled.div`
  font-family: "Urbanist", "Inter", "Segoe UI", sans-serif;
  font-size: 13.5px;
  line-height: 1.65;
  color: #444;

  p { margin: 0 0 9px; }
  h3, h4 {
    font-family: "Poppins", "Urbanist", sans-serif;
    font-weight: 700;
    color: #042940;
    margin: 13px 0 7px;
  }
  h3 { font-size: 15px; }
  h4 { font-size: 13.5px; }
  ul, ol { margin: 0 0 9px; padding-left: 24px; }
  li { margin-bottom: 6px; padding-left: 2px; }
  ul > li::marker { color: #84b026; font-size: 1.25em; font-weight: 700; }
  ol > li::marker { color: #84b026; font-weight: 700; }
  blockquote {
    border-left: 3px solid #84b026;
    margin: 10px 0;
    padding: 6px 14px;
    color: #555;
    background: rgba(132, 176, 38, 0.07);
    border-radius: 0 8px 8px 0;
  }
  a { color: #0b6aa9; text-decoration: underline; }
  > *:last-child { margin-bottom: 0; }
`;

const PriceSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #042940 0%, #03506f 100%);
  border-radius: 10px;
  margin: 18px 0;
  box-shadow: 0 4px 16px rgba(4, 41, 64, 0.2);
`;

const PriceLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Poppins', sans-serif;
`;

const PriceValue = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const QuantitySection = styled.div`
  margin: 18px 0;
`;

const QuantityLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
  font-family: 'Poppins', sans-serif;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: #f8f9fa;
  padding: 10px 20px;
  border-radius: 50px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: fit-content;
  margin: 0 auto;
`;

const QuantityButton = styled(IconButton)`
  width: 40px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &.decrease {
    background: linear-gradient(135deg, #fee 0%, #fdd) !important;
    color: #e74c3c !important;
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%) !important;
      color: white !important;
      transform: scale(1.15) rotate(-90deg);
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
    }
  }
  
  &.increase {
    background: linear-gradient(135deg, #e8f4f8 0%, #d0e7f0) !important;
    color: #042940 !important;
    
    &:hover {
      background: linear-gradient(135deg, #042940 0%, #03506f 100%) !important;
      color: white !important;
      transform: scale(1.15) rotate(90deg);
      box-shadow: 0 4px 12px rgba(4, 41, 64, 0.4);
    }
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #042940;
  min-width: 36px;
  text-align: center;
  font-family: 'Poppins', sans-serif;
`;

const ActionButtons = styled.div`
  margin-top: 24px;
`;

const ViewDetailsLink = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  background: none;
  border: none;
  color: #0b6aa9;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Poppins', sans-serif;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #eef6fb;
    text-decoration: underline;
  }
`;

const AddToCartButton = styled.button`
  width: 100%;
  padding: 16px 28px;
  background: linear-gradient(135deg, #84b026 0%, #6a9020 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 6px 20px rgba(132, 176, 38, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  .icon {
    font-size: 20px;
    transition: transform 0.3s ease;
    background: rgba(255, 255, 255, 0.92);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6a9020 0%, #84b026 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(132, 176, 38, 0.5);
    
    .icon {
      transform: scale(1.2) rotate(10deg);
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &.in-cart {
    background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
    box-shadow: 0 6px 20px rgba(39, 174, 96, 0.4);
    cursor: default;
    
    .in-cart-icon {
      background: rgba(255, 255, 255, 0.25) !important;
      animation: checkBounce 0.6s ease;
    }
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: default;
  }
  
  @keyframes checkBounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.3); }
  }
`;

const GoToCartButton = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 13px 28px;
  background: #ffffff;
  color: #229954;
  border: 2px solid #229954;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #229954;
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(39, 174, 96, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-height: 450px;
  background: #000;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #f24405 0%, #042940 100%);
    z-index: 1;
  }

  @media (max-width: 768px) {
    max-height: 250px;
  }
`;

const MediaWrapper = styled.div`
  width: 100%;
  height: 450px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;

  @media (max-width: 768px) {
    height: 250px;
    padding: 10px;
  }
`;

const MediaImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: black;

  .cld-video-player {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const YouTubeEmbedFrame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  z-index: 1;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.5);
  }

  svg {
    color: white;
    transition: transform 0.2s;
    filter: drop-shadow(0 0 8px rgba(0, 0, 0, 0.3));
  }

  &:hover svg {
    transform: scale(1.2);
  }
`;
